import { BaseType, PaginatedResults } from 'types';
import { ProteinMachineStateCategoryName } from 'types/protein';
import { Value } from 'components/KPICard/CardComponents';

/**
 * DSI
 */
export type DSIKPIInterval =
  | 'Last 15 min'
  | 'Last 30 min'
  | 'Last hour'
  | 'Last Shift'
  | 'Last Day'
  | 'Last Week'
  | 'Last Month'
  | 'Current Day';

export enum DSIKPIType {
  DsiCurrentKpi = 'DsiCurrentKpi',
  InfeedPieceSize = 'InfeedPieceSize',
  ThroughputRate = 'ThroughputRate',
  ThroughputPieceCount = 'ThroughputPieceCount',
  BeltSpeed = 'BeltSpeed',
  Loading = 'Loading',
  LoadingEfficiency = 'LoadingEfficiency',
  LoadingGap = 'LoadingGap',
  Yield = 'Yield',
  PressurizeAverageTime = 'Pressurization Time - Average',
  UnnecessaryPressurizationTime = 'Unnecessary Pressurization Time',
  ProductProcessed = 'ProcessedProductPercentage',
  OutputWeight = 'OutputWeight',
  OutputPiece = 'OutputPiece',
  PumpPressure = 'PumpPressure',
  MachineInfoOEE = 'MachineInfoOEE',
  PsuName = 'PsuName'
}
export enum DSIKpiId {
  CurrentPSU = 'psu_name',
  App = 'app_name',
  InfeedPiece = 'infeed_piece_size',
  ProductType = 'product_type',
  OeeCumulative = 'oee_cumulative',
  OeeAvailability = 'oee_availability_cumulative',
  OeePerformance = 'oee_performance_cumulative',
  OeeQuality = 'oee_quality_cumulative',
  StateName = 'state_name'
}

export enum SiteTableDataType {
  OEE = 'oee',
  Availability = 'availability',
  Performance = 'performance',
  Quality = 'quality'
}

export interface DSISiteTableData extends BaseType {
  id: string;
  description: string;
  lineName: string;
  status: ProteinMachineStateCategoryName;
  numCurrentAlarms: number;
  numAlarmsOverPeriod: number;
  productionOverPeriod: number;
  utilizationOverPeriod: number;
  oee: string;
  psu: string;
}

export type MachineKpiItem = {
  title: string;
  kpiType: string;
  value1?: Value;
  value2?: Value;
  value3?: Value;
  status?: string;
  targetStatus?: { label: string; value: string };
  values?: Value[];
};

export enum BusinessUnit {
  DSI = 'dsi',
  PROTEIN = 'protein',
  PASCAL = 'pascal',
  AVURE = 'avure'
}

export enum MachineHealthInterval {
  Last8Hours = 'LAST_8_HOURS',
  Last7Days = 'LAST_7_DAYS',
  Last3Days = 'LAST_3_DAYS',
  Last15Min = 'LAST_15_MINUTES',
  Last30Min = 'LAST_30_MINUTES',
  LastHour = 'LAST_HOUR',
  Last3Hours = 'LAST_3_HOURS',
  LastShift = 'LAST_SHIFT',
  LastDay = 'LAST_DAY',
  LastWeek = 'LAST_WEEK',
  LastMonth = 'LAST_MONTH',
  CurrentDay = 'CURRENT_DAY'
}

export enum MachineStateCodes {
  Offline = '1',
  NoProduct = '0',
  Running = '2',
  NotRunning = '5'
}

export enum MachineStateNames {
  Offline = 'Offline',
  NoProduct = 'No Product',
  Running = 'Running',
  NotRunning = 'Not Running'
}

export interface MachineStateRequestParams extends BaseType {
  machineId: string;
  startDatetime?: string;
  endDatetime?: string;
}

export interface DSIConfiguration extends BaseType  {
  cosmos_product_setups?: string[];
  postgres_product_setups?: string[];
}

export interface DSIConfigurationQueryParams {
  machineId?: string; // string($uuid) (path)
  machine_id?: string; // string($uuid) (path)
  data_source?: string | 'all' | 'postgres' | 'cosmos'; // (query)
  //kpi_data_interval?: 'LAST_MINUTE' | 'LAST_5_MINUTES' | 'LAST_15_MINUTES' | 'LAST_30_MINUTES' | 'LAST_HOUR' | 'LAST_3_HOURS' | 'LAST_4_HOURS' | 'LAST_8_HOURS' | 'LAST_DAY' | 'LAST_3_DAYS' | 'LAST_7_DAYS' | 'LAST_WEEK' | 'LAST_MONTH' | 'LAST_SHIFT' | 'CURRENT_DAY'; // (query)
  utc_start_datetime?: string; // string($date-time) (query)
  utc_end_datetime?: string; // string($date-time) (query)
  }

export type MachineIdParams = {
  machineId: string;
};

export type ConfigTableName =
  | 'aseptics-alarm-config'
  | 'aseptics-alarm-omniblu'
  | 'aseptics-plc-params'
  | 'aseptics-strings'
  | 'aseptics-variables'
  | 'aseptics-modes'
  | 'machine-configuration-details'
  | 'aseptic-alarm-categories'
  | 'aseptic-machine-modes'
  | 'aseptic-machine-sensor-configs';

export type ConfigTableByMachineIdParams = {
  machineId: string;
  tableName: ConfigTableName;
  query?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export type ImplementedStaticDatasetKey =
  | 'aseptics_alarm_config'
  | 'aseptics_alarm_omniblu'
  | 'aseptics_plc_params'
  | 'aseptics_strings'
  | 'aseptics_variables'
  | 'machine_configuration_details'
  | 'aseptic_alarm_categories'
  | 'aseptic_machine_modes'
  | 'aseptic_machine_sensor_configs';

export type StaticDatasetKey = ImplementedStaticDatasetKey | string;

export type StaticDatasetDefinition = BaseType & {
  dataset_key?: string;
  datasetKey?: string;
  name?: string;
  columns?: BaseType[];
};

export type StaticDatasetsResponse =
  | StaticDatasetDefinition[]
  | {
      datasets?: StaticDatasetDefinition[];
      supported_datasets?: StaticDatasetDefinition[];
      supportedDatasets?: StaticDatasetDefinition[];
    };

export type StaticDatasetByMachineParams = {
  machineId: string;
  datasetKey: StaticDatasetKey;
};

export type ImportStaticDatasetArgs = StaticDatasetByMachineParams & {
  file: File;
};

export type SfMachineIdParams = {
  sfMachineId: string;
};

export type SalesforceMachineIdParams = {
  salesforceMachineId: string;
};

export type RecordIdParams = {
  recordId: string;
};

export type VarIdParams = {
  varId: string;
};

export interface AsepticsAlarmConfigRecord extends BaseType {
  machine_id: string;
  number?: number;
  symbolname?: string;
  symbolfullname?: string;
  actionmode1?: number;
  actionmode2?: number;
  actionmode3?: number;
  actionmode4?: number;
  actionmode5?: number;
  actionmode6?: number;
  actionmode7?: number;
  actionmode8?: number;
  actionmode9?: number;
  actionmode10?: number;
  ignoreaction?: boolean;
  image?: string;
  stop?: number;
  sendtoplc?: string;
  iops_id?: number;
  typestatus?: string;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticsAlarmOmnibluRecord extends BaseType {
  machine_id: string;
  alarm_id?: number;
  capacity_loss?: number;
  machine_module?: number;
  lane?: number;
  widget1?: number;
  widget2?: number;
  widget3?: number;
  widget4?: number;
  widget5?: number;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticsPlcParamsRecord extends BaseType {
  machine_id: string;
  number?: number;
  description?: string;
  symbolname?: string;
  symbolfullname?: string;
  type?: number;
  mainfunction?: number;
  subfunction?: number;
  parammode?: number;
  userlevel?: number;
  default0?: number;
  minimum0?: number;
  maximum0?: number;
  unit0?: string;
  minimum1?: number;
  maximum1?: number;
  unit1?: string;
  minimum2?: number;
  maximum2?: number;
  unit2?: string;
  state?: number;
  loggingmode?: number;
  lane?: number;
  iops_id?: number;
  typestatus?: string;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticsStringsRecord extends BaseType {
  machine_id: string;
  kind?: number;
  number?: number;
  english?: string;
  english_usa?: string;
  iops_id?: number;
  typestatus?: string;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticsVariablesRecord extends BaseType {
  machine_id: string;
  mode?: number;
  number?: number;
  item?: number;
  variable?: string;
  iops_id?: number;
  typestatus?: string;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface MachineConfigurationDetailRecord extends BaseType {
  machine_id?: string;
  configuration_id: number;
  sequence_number: number;
  recipe: string;
  recipe_descr?: string;
  mode?: string;
  mode_descr?: string;
  value_hours: string;
  tracker?: string;
  value_3?: string;
  value_4?: string;
  id: number;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticAlarmCategoryRecord extends BaseType {
  machine_id: string;
  machinemodule: number;
  english: string;
  color_code?: string;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticsModesRecord extends BaseType {
  machine_id: string;
  number?: number;
  description?: string;
  mode?: number;
  saved?: number;
  plcsend?: number;
  loginterval?: number;
  iops_id?: number;
  typestatus?: string;
  id: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticMachineModeRecord extends BaseType {
  id: string;
  machine_id: string;
  steps: Record<string, unknown>;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}
export interface AsepticMachineSensorConfigRecord extends BaseType {
  id: string;
  var_id: string;
  description: string;
  type?: string;
  machine_id: string;
  value_definitions: Record<string, unknown>;
  machine_type?: string;
  valid_from: string;
  valid_to?: string;
  created_at: string;
  created_by: string;
}

export type ConfigTableResponseByName = {
  'aseptics-alarm-config': AsepticsAlarmConfigRecord[];
  'aseptics-alarm-omniblu': AsepticsAlarmOmnibluRecord[];
  'aseptics-plc-params': AsepticsPlcParamsRecord[];
  'aseptics-strings': AsepticsStringsRecord[];
  'aseptics-variables': AsepticsVariablesRecord[];
  'aseptics-modes': AsepticsModesRecord[];
  'machine-configuration-details': MachineConfigurationDetailRecord[];
  'aseptic-alarm-categories': AsepticAlarmCategoryRecord[];
  'aseptic-machine-modes': AsepticMachineModeRecord[];
  'aseptic-machine-sensor-configs': AsepticMachineSensorConfigRecord[];
};

export type ConfigTableResponse = PaginatedResults<
  ConfigTableResponseByName[ConfigTableName][number]
>;

export type UpdateByRecordIdArgs<T extends BaseType> = {
  recordId: string;
  payload: Partial<T>;
};

export type UpdateByVarIdArgs<T extends BaseType> = {
  varId: string;
  payload: Partial<T>;
};

export type UpdateAsepticMachineModesArgs = {
  machineId: string;
  payload: BaseType | BaseType[];
};